/**
 * Product card colour-swatch swap.
 *
 * When a shopper clicks a colour swatch on a product card
 * (`<a class="product-card__colour" data-js="product-card-colour"
 *   data-handle="...">`), instead of navigating to the sibling
 * product, fetch its data as JSON via the alt template
 * `templates/product.json.liquid` (`/products/{handle}?view=json`),
 * clone the global `<template data-js="product-card-template">`,
 * apply the binder, and replace the current card in place.
 *
 * Going JSON-not-HTML keeps a single source of truth for the
 * card markup (the `<template>`), and means the same render path
 * runs whether the data came from Shopify or Searchspring.
 *
 * Notes:
 *   - Every swatch is clickable; inventory is the concern of the
 *     quick-add drawer / product page, not the card swatches.
 *   - Document-level event delegation, so this works for both
 *     server-rendered cards (`product-card.liquid`) and the
 *     hydrated cards rendered from the same template.
 *   - The swatch stays an `<a>`. Only plain left-clicks are
 *     intercepted; cmd/ctrl/middle/shift-click and right-click
 *     fall through to native nav. No-JS users get a real link.
 *   - Failed fetches leave the original card untouched and evict
 *     the cache so a later click can retry; the clicked swatch shows
 *     a transient error tooltip so the click doesn't read as broken.
 *   - Swapping to a fully sold-out colour still happens (so notify-me /
 *     wishlist stay reachable); the new card's active swatch flashes a
 *     "sold out" tooltip so the stock status is clear.
 *   - Fires `product-card:swapped` on the new card so wishlist
 *     hydration / analytics can re-bind without coupling here.
 */
(function () {
  if (window.__productCardSwapInitialised) return;
  window.__productCardSwapInitialised = true;

  const SWATCH_SELECTOR = '[data-js="product-card-colour"]';
  const CARD_SELECTOR = ".product-card";
  const TEMPLATE_SELECTOR = 'template[data-js="product-card-template"]';

  /** @type {Map<string, Promise<object>>} */
  const cache = new Map();

  const TOOLTIP_VISIBLE_MS = 3500;

  /** @type {HTMLElement | null} */
  let swatchTooltip = null;
  let tooltipTimer = 0;

  /**
   * Lazily create the shared swatch tooltip. One body-level element is
   * reused across every card; living outside the cards keeps a carousel
   * rail's `overflow: hidden` from clipping it.
   * @returns {HTMLElement}
   */
  function getSwatchTooltip() {
    if (swatchTooltip) return swatchTooltip;
    const el = document.createElement("div");
    el.className = "product-card__swatch-notice";
    el.dataset.js = "swatch-notice";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.dataset.visible = "false";
    document.body.appendChild(el);
    swatchTooltip = el;
    return el;
  }

  /**
   * Hide the swatch tooltip if it is showing.
   * @returns {void}
   */
  function hideSwatchTooltip() {
    if (swatchTooltip) swatchTooltip.dataset.visible = "false";
  }

  /**
   * Show a transient tooltip anchored to a swatch. Copy is passed in
   * (localised in Liquid via the swatch container's `data-i18n-*`
   * attributes) so no user-facing text is hardcoded here; with no swatch
   * or message we stay silent. `variant` selects the styling. Positioned
   * `fixed` from the swatch rect and dismissed on the next scroll/resize,
   * which invalidates the anchor.
   * @param {HTMLElement} swatch - The swatch to anchor to.
   * @param {string} message - The localised message to show.
   * @param {string} variant - Tooltip style: 'error' or 'sold-out'.
   * @returns {void}
   */
  function showSwatchTooltip(swatch, message, variant) {
    if (!swatch || !message) return;

    const tip = getSwatchTooltip();
    tip.textContent = message;
    tip.dataset.variant = variant;
    tip.dataset.visible = "false";

    const swatchRect = swatch.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const gap = 8;
    const margin = 8;

    let placement = "above";
    let top = swatchRect.top - tipRect.height - gap;
    if (top < margin) {
      placement = "below";
      top = swatchRect.bottom + gap;
    }

    const centre = swatchRect.left + swatchRect.width / 2;
    const maxLeft = Math.max(margin, window.innerWidth - tipRect.width - margin);
    const left = Math.min(Math.max(centre - tipRect.width / 2, margin), maxLeft);

    tip.dataset.placement = placement;
    tip.style.top = Math.round(top) + "px";
    tip.style.left = Math.round(left) + "px";
    tip.style.setProperty("--caret-x", Math.round((centre - left) - 5) + "px");
    tip.dataset.visible = "true";

    if (tooltipTimer) clearTimeout(tooltipTimer);
    tooltipTimer = setTimeout(hideSwatchTooltip, TOOLTIP_VISIBLE_MS);

    window.addEventListener("scroll", hideSwatchTooltip, {
      once: true,
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", hideSwatchTooltip, { once: true });
  }

  /**
   * Read a localised message off the swatch's container and show the
   * matching tooltip variant. No-ops when the swatch has no container or
   * the message attribute is absent, so a missing string degrades to
   * silence rather than empty chrome.
   * @param {HTMLElement} swatch - The swatch to anchor to.
   * @param {string} datasetKey - Container dataset key holding the copy.
   * @param {string} variant - Tooltip style: 'error' or 'sold-out'.
   * @returns {void}
   */
  function showSwatchMessage(swatch, datasetKey, variant) {
    const container = swatch && swatch.closest('[data-js="colour-swatches"]');
    if (!container) return;
    showSwatchTooltip(swatch, container.dataset[datasetKey], variant);
  }

  /**
   * @param {MouseEvent} event
   * @returns {boolean}
   */
  function isPlainLeftClick(event) {
    return (
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  /**
   * Fetch (and memoise) the JSON for `handle`. Failed responses are
   * evicted so a later click can retry from scratch. The endpoint sets
   * `Content-Type: text/html`, so we read as text and parse —
   * `response.json()` would refuse the wrong MIME.
   *
   * Note: we deliberately do NOT send `Accept: application/json`.
   * Shopify content-negotiates product URLs and will return the
   * built-in `{ "product": { ... } }` JSON shape (ignoring
   * `?view=json`) if that header is present, which would break
   * the bindings. Letting the browser default to `Accept: /`
   * (or text/html) ensures the alt template renders.
   * @param {string} handle
   * @returns {Promise<object>}
   */
  function fetchCardData(handle) {
    const cached = cache.get(handle);
    if (cached) return cached;
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    const promise = fetch(
      root + "products/" + encodeURIComponent(handle) + "?view=json",
      { credentials: "same-origin" },
    )
      .then((response) => {
        if (!response.ok) throw new Error("status_" + response.status);
        return response.text();
      })
      .then((text) => JSON.parse(text));
    cache.set(handle, promise);
    promise.catch(() => cache.delete(handle));
    return promise;
  }

  /**
   * Build a populated card element from the global template +
   * the data for `handle`. Returns null if the template is
   * missing (defensive — keeps the original card in place).
   * @param {object} data
   * @param {HTMLElement} card
   * @returns {Element | null}
   */
  function renderCard(data, card) {
    if (
      !window.ProductCardBind ||
      typeof window.ProductCardBind.renderCard !== "function"
    ) {
      return null;
    }
    let template = null;
    const context = card.closest("[data-card-template-selector]");
    if (context && context.dataset.cardTemplateSelector) {
      template = document.querySelector(context.dataset.cardTemplateSelector);
    }
    if (!template) {
      template = document.querySelector(TEMPLATE_SELECTOR);
    }
    if (!template) return null;
    return window.ProductCardBind.renderCard(template, data);
  }

  /**
   * Replace `card` with the freshly-rendered card for `handle`.
   * No-ops if the card is mid-swap or already showing this handle.
   * @param {HTMLElement} card
   * @param {string} handle
   * @param {HTMLElement} [swatch] - The clicked swatch, when triggered by a click.
   * @returns {Promise<void>}
   */
  async function swapCard(card, handle, swatch) {
    if (card.dataset.swapping === "true") return;
    if (card.dataset.productHandle === handle) return;
    card.dataset.swapping = "true";

    // Optimistic feedback: activate the clicked swatch immediately;
    // reverted if the swap can't complete.
    const previousActive = card.querySelector(
      '[data-js="product-card-colour"][data-active="true"]',
    );
    if (swatch && swatch !== previousActive) {
      if (previousActive) previousActive.dataset.active = "false";
      swatch.dataset.active = "true";
    }

    /**
     * Undo the optimistic swatch highlight so the card is never left
     * showing one colour with a different swatch marked active.
     * @returns {void}
     */
    function revertOptimistic() {
      if (swatch && swatch !== previousActive) {
        swatch.dataset.active = "false";
        if (previousActive) previousActive.dataset.active = "true";
      }
    }

    // Capture the expanded state of colour swatches before replacing
    // the card, so we can restore it on the new card.
    const oldSwatches = card.querySelector('[data-js="colour-swatches"]');
    const wasExpanded = oldSwatches && oldSwatches.dataset.expanded === "true";

    try {
      const data = await fetchCardData(handle);
      const newCard = renderCard(data, card);
      if (!newCard) {
        revertOptimistic();
        card.dataset.swapping = "false";
        showSwatchMessage(swatch, "i18nError", "error");
        return;
      }
      // Preserve the inner slider's swipeable context. The product-JSON
      // endpoint always reports `swipeable: true` (correct for collection
      // grids), but inside a carousel the media slider must stay
      // non-swipeable so horizontal drags reach the parent rail instead of
      // being hijacked by the card's own drag handler. The card being
      // replaced already encodes the right context, so mirror its state onto
      // the replacement before it connects and reads the attribute.
      const oldSlider = card.querySelector("media-slider");
      const newSlider = newCard.querySelector("media-slider");
      if (oldSlider && newSlider) {
        if (oldSlider.hasAttribute("data-swipeable")) {
          newSlider.setAttribute("data-swipeable", "");
        } else {
          newSlider.removeAttribute("data-swipeable");
        }
      }

      // Replace the article wholesale. Custom elements inside the
      // new fragment (`<media-slider>`) initialise themselves via
      // connectedCallback once attached, so no manual hand-off
      // is required here.
      card.replaceWith(newCard);

      const newSwatch = newCard.querySelector(
        `[data-js="product-card-colour"][data-handle="${handle}"]`,
      );
      if (newSwatch) newSwatch.dataset.active = "true";

      // Restore the expanded state so swatches don't collapse on swap.
      if (wasExpanded) {
        const newSwatches = newCard.querySelector(
          '[data-js="colour-swatches"]',
        );
        if (newSwatches) newSwatches.dataset.expanded = "true";
      }

      const slider = newCard.querySelector("media-slider");
      const inProductMode =
        newCard.closest('[data-display-mode="product"]') !== null;
      if (
        slider &&
        inProductMode &&
        typeof slider.goToOriginalIndex === "function"
      ) {
        const slideCount = slider.querySelectorAll(
          ".media-slider__slide",
        ).length;
        if (slideCount > 1) {
          slider._preProductIndex = "0";
          slider.goToOriginalIndex(String(slideCount - 1), { instant: true });
        }
      }

      // Trigger the active slide image to swap from `data-src` to
      // `src`. In the regular grid path this is driven by
      // `lazyLoadCards()` in `sections/collection.liquid` via an
      // IntersectionObserver, but the swapped card is appended
      // already-in-view so that observer never fires for it.
      if (slider && typeof slider.loadActive === "function") {
        slider.loadActive();
      }
      newCard.dispatchEvent(
        new CustomEvent("product-card:swapped", {
          bubbles: true,
          detail: { handle, data },
        }),
      );

      // A fully sold-out colour still swaps in — so the shopper can see it
      // and reach notify-me / wishlist — but flash a tooltip on the new
      // card's active swatch so the stock status isn't a silent surprise.
      if (data && data.available === false) {
        showSwatchMessage(newSwatch, "i18nSoldOut", "sold-out");
      }
    } catch (_err) {
      // Leave the original card intact so the shopper can click again
      // or fall through to native nav. Undo the optimistic highlight and
      // surface a tooltip so the failure doesn't read as a dead swatch.
      revertOptimistic();
      card.dataset.swapping = "false";
      showSwatchMessage(swatch, "i18nError", "error");
    }
  }

  /**
   * @param {Event} event
   */
  function onClick(event) {
    const swatch =
      event.target.closest && event.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;
    if (!isPlainLeftClick(event)) return;
    const handle = swatch.dataset.handle;
    if (!handle) return;
    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;
    event.preventDefault();
    swapCard(card, handle, swatch);
  }

  document.addEventListener("click", onClick);

  document.addEventListener("product-card:request-swap", (e) => {
    const { card, handle } = e.detail || {};
    if (card && handle) swapCard(card, handle);
  });

  // Makes the product card swap cache globally available. Reduces network requests for the same product card.
  window.ProductCardSwap = {
    fetchCardData,
  };
})();
